<script setup>
import { ref, reactive, onMounted, computed, watch } from "vue";
import { apiQuery } from "@/api/j24api";
import { useTheme } from "vuetify";
import { appBarStore } from "@/stores/appBar";
import AlertsList from "@/components/AlertsList.vue";
import AppView from "./components/AppView.vue";
import LoadingStatus from "./components/LoadingStatus.vue";
import AppIcon from "./components/AppIcon.vue";

const appBar = appBarStore();
const theme = useTheme();
const selectedLicense = ref({});
const loading = ref(false);
const alerts = ref([]);
const report = reactive([]);
const searchField = ref("");
const selectedTab = ref("Loading");
const selectedApp = ref({});
const apps = reactive([]);
const devices = reactive([]);
const commands = reactive([]);
const reportDate = ref(null);
const forceRefresh = ref(false);

const loadingDevices = ref("Loading");
const loadingApps = ref("Loading");
const loadingReport = ref("Loading");
const loadingCommands = ref("Loading");

const includeAllApps = ref(false);
const filterMenu = ref(false);

async function getData() {
  loading.value = true;
  selectedTab.value = "Loading";
  loadingDevices.value = "Loading";
  loadingApps.value = "Loading";
  loadingReport.value = "Loading";
  // Run getAllApps, getReport, and getDevices in parallel
  const [appsResult, reportResult, devicesResult, commandsResult] =
    await Promise.all([
      getAllApps(),
      getReport(),
      getDevices(),
      getDeviceCommands(),
    ]);
  if (
    loadingDevices.value === "OK" &&
    loadingApps.value === "OK" &&
    loadingReport.value === "OK" &&
    loadingCommands.value === "OK"
  ) {
    // If all three functions were successful, set loading to false
    loading.value = false;
    selectedTab.value = "Overview";
  }
}

async function getDeviceCommands() {
  loadingCommands.value = "Loading";
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getDeviceCommands",
      licenseId: selectedLicense.value._id,
    });
    if (result.result) {
      loadingCommands.value = "OK";
      commands.length = 0;
      commands.push(...result.result);
      return;
    }
  } catch (error) {
    console.error("Error fetching device commands:", error);
    loadingCommands.value = "Error";
  }
  loadingCommands.value = "Failed";
}

async function getDevices() {
  // Run getDashboardData and getPatchDevices in parallel
  const [dashboardData, patchDevices] = await Promise.all([
    getDashboardData(),
    getPatchDevices(),
  ]);
  if (dashboardData && patchDevices) {
    // Process the data as needed
    //console.log("Dashboard Data:", dashboardData);
    //console.log("Patch Devices:", patchDevices);
    if (selectedLicense.value.mdmServerID) {
      let processed = processDevicesInsights(
        dashboardData.devices,
        patchDevices
      );
      //console.log("Processed Devices:", processed);
      devices.length = 0;
      devices.push(...processed);
      loadingDevices.value = "OK";
    } else {
      devices.length = 0;
      devices.push(...processDevices(patchDevices));
      loadingDevices.value = "OK";
    }
  } else {
    alerts.value.push({
      type: "error",
      message: "Error getting devices",
      closable: true,
    });
    loadingDevices.value = "Error";
  }
}

async function getPatchDevices() {
  try {
    //console.log("selectedLicense", selectedLicense);
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getDevices",
      id: selectedLicense.value._id,
      minimal: true,
    });
    if (result.length) {
      return result;
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting devices",
      closable: true,
    });
  }
}

async function getDashboardData() {
  try {
    var dashboard;
    var devices;
    if (selectedLicense.value.mdmServerID) {
      // First get dashboard data
      const dashboard = await apiQuery("ELA", "cmd24/query", {
        dataRequested: "dashboard",
        maxage: 86400,
        mdmServerId: selectedLicense.value.mdmServerID,
      });
      if (dashboard.content?.macOSAll?.records) {
        const devicesResult = await apiQuery("ELA", "cmd24/query", {
          dataRequested: "dashboardDetail",
          id: dashboard.content.macOSAll.records,
          mdmServerId: selectedLicense.value.mdmServerID,
        });
        if (devicesResult.content.length) {
          devices = [];
          devices.push(...devicesResult.content);
        }
      }
    }
    return {
      devices: devices,
      dashboard: dashboard,
    };
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting devices",
      closable: true,
    });
  }
}

function processDevices(pdevices) {
  let result = [];
  pdevices.forEach((device) => {
    var newDevice = device;
    newDevice.Computer_Name = device.computerName;
    newDevice.Serial_Number = device.serial;
    newDevice.Model = device.stats?.HardwareStats?.computerModel || "";
    newDevice.patchLastContact = device.lastContact;
    newDevice.updatesAvailableCount = device.updatesAvailable?.length
      ? device.updatesAvailable.length
      : 0;
    // newDevice.updatesAvailable = device.updatesAvailable?.length
    //   ? device.updatesAvailable
    //   : 0;
    // newDevice.patchLastContact = device.lastContact;
    // newDevice.updatesAvailableCount = device.updatesAvailable?.length
    //   ? device.updatesAvailable.length
    //   : 0;
    result.push(newDevice);
  });
  return result;
}

function processDevicesInsights(devices, pdevices) {
  let result = [];
  devices.forEach((device) => {
    let found = pdevices.find(
      (pdevice) => pdevice.serial === device.Serial_Number
    );
    if (found) {
      var newDevice = device;
      newDevice.patchData = found;
      newDevice.updatesAvailable = found.updatesAvailable?.length
        ? found.updatesAvailable
        : 0;
      newDevice.patchLastContact = found.lastContact;
      newDevice.updatesAvailableCount = found.updatesAvailable?.length
        ? found.updatesAvailable.length
        : 0;
      result.push(newDevice);
    }
  });
  return result;
}

watch(includeAllApps, async (newValue) => {
  loading.value = true;
  await getReport();
  loading.value = false;
});

onMounted(async () => {
  theme.global.name.value = "jigsawNew";
  if (appBar.selected) {
    await getLicense(appBar.selected);
    await getData();
  } else {
    selectedTab.value = "Select License";
  }
});

appBar.$subscribe(async () => {
  //console.log("Appbar changed", appBar.$state);
  if (appBar.selected) {
    await getLicense(appBar.selected);
    await getData();
  }
});

let headers = [
  {
    title: "App Name",
    value: "application",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
  },
  {
    title: "Patching",
    value: "patching",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
  },
  {
    title: "Latest Version",
    value: "latestVersion",
    headerProps: { style: "font-weight: bold" },
  },
  {
    title: "App Installed On",
    value: "noOfDevices",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
  },
  {
    title: "Compliance Level",
    value: "compliancePercentage",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
  },
];

async function getAllApps() {
  loadingApps.value = "Loading";
  const result = await apiQuery("ELA", "patching/patchingManagement", {
    action: "getAppsList",
  });
  if (result.length) {
    apps.length = 0;
    apps.push(...result);
    loadingApps.value = "OK";
    return;
  }
  loadingApps.value = "Error";
}

const filteredReport = computed(() => {
  return report;
});

async function getReport() {
  loadingReport.value = "Loading";
  try {
    if (!selectedLicense.value) {
      return;
    }
    var result = await apiQuery("ELA", "dashboard/patchingComplianceReport", {
      apiAction: "GetPatchComplianceReportV3",
      instance: selectedLicense.value._id,
      includeAllApps: includeAllApps.value,
      cache: true,
      forceRefresh: forceRefresh.value,
      // maxInventoryAge: 5,
      // complianceMaxAge: 14,
    });
    //console.log("Report result", result);

    // If it is triggering a new report to be generated.
    if (result.cacheId) {
      loadingReport.value = "Generating Report";
      let maxAttempts = 30;
      var attempt = 0;
      while (attempt < maxAttempts) {
        let reportStatus = await apiQuery(
          "ELA",
          "dashboard/patchingComplianceReport",
          {
            apiAction: "GetReportCache",
            cacheId: result.cacheId,
            instance: selectedLicense.value._id,
          }
        );
        if (reportStatus.statusMessage) {
          loadingReport.value = reportStatus.statusMessage;
        }
        if (reportStatus.data) {
          result = reportStatus.data;
          break;
        }
        await sleep(500);
        attempt++;
      }
    }
    console.log("Final Report result", result);

    // Update report date - check both createdAt and cacheDate
    // Use current time if force refresh was triggered and no dates provided
    if (result.createdAt) {
      reportDate.value = new Date(result.createdAt);
    } else if (result.cacheDate) {
      reportDate.value = new Date(result.cacheDate);
    } else if (forceRefresh.value) {
      // If force refresh was triggered but no date returned, use current time
      reportDate.value = new Date();
    }

    if (result.report) {
      report.length = 0;
      report.push(...result.report);
    }
    if (result.allBundleIds && result.allBundleIds.length) {
      report.push(...result.allBundleIds);
    }
  } catch (error) {
    console.error("Error getting report", error);
    loadingReport.value = "Error";
    return;
  }
  result.report.forEach((item) => {
    //console.log("Item", item);
    if (!Object.prototype.hasOwnProperty.call(item, "patching")) {
      //console.log("Item not patching", item);
    }
  });
  loadingReport.value = "OK";
}

async function getLicense(id) {
  loading.value = true;
  try {
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingLicense",
      id: id,
    });
    if (result.length) {
      selectedLicense.value = result[0];
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting license",
        closable: true,
      });
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting license",
      closable: true,
    });
  }
}

function rowClick(item, row) {
  //console.log("Row Clicked", item, row);
  selectedApp.value = row.item;
  selectedTab.value = "AppView";
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function forceRefreshData() {
  forceRefresh.value = true;
  await getData();
  forceRefresh.value = false;
}

function relativeTimeFromNow(date) {
  if (!date) return "";
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return `${diffSec} second${diffSec === 1 ? "" : "s"} ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay <= 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

  return then.toLocaleString();
}
</script>

<template>
  <v-window v-if="appBar.selected" v-model="selectedTab">
    <v-window-item value="Overview">
      <h1 class="mb-3">App Management</h1>

      <v-row class="px-1 mb-1">
        <v-col cols="3">
          <v-text-field
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            v-model="searchField"
            placeholder="Search anything..."
            variant="solo"
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="auth">
          <v-btn
            :append-icon="filterMenu ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            v-model="filterMenu"
            size="large"
            height="40"
            class="text-none"
            >Filter
            <v-menu activator="parent" v-model="filterMenu">
              <v-list>
                <v-list-item>
                  <v-list-item-action>
                    <v-checkbox
                      v-model="includeAllApps"
                      label="Include All Apps"
                      hide-details
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-btn>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto">
          <v-tooltip text="Click to Refresh report" location="top">
            <template #activator="{ props }">
              <v-btn
                class="text-none"
                @click="forceRefreshData"
                prepend-icon="mdi-refresh"
                variant="elevated"
                height="40"
                size="large"
                v-bind="props"
              >
                Report Created: {{ relativeTimeFromNow(reportDate) }}
              </v-btn>
            </template>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-sheet rounded="lg" elevation="1" class="ma-1">
        <v-data-table
          select-strategy="single"
          :items-per-page="25"
          :search="searchField"
          :headers="headers"
          :items="report"
          :loading="loading"
          :no-data-text="loading ? 'Loading...' : 'No data found'"
          hover
          @click:row="rowClick"
        >
          <template v-slot:[`item.application`]="{ item }">
            <v-row align="center">
              <v-col cols="auto">
                <AppIcon :appID="item.id" />
              </v-col>
              <v-col cols="auto">
                {{ item.application }}
              </v-col>
            </v-row>
          </template>

          <template v-slot:[`item.patching`]="{ item }">
            <v-chip
              v-if="item.patching"
              :color="item.patching ? 'green' : 'red'"
              >{{ item.patching ? "YES" : "NO" }}</v-chip
            >

            <v-chip
              v-if="item.unsupportedApp"
              rounded="xl"
              color="red"
              readonly
              size="small"
              >UNSUPPORTED APP</v-chip
            >
          </template>

          <template v-slot:[`item.compliancePercentage`]="{ item }">
            <div v-if="item.noOfDevices == 0 || item.unsupportedApp">N/A</div>
            <div v-else class="d-flex">
              <div>
                <v-sheet class="mt-1" :width="150">
                  <v-progress-linear
                    minwidth="100"
                    v-model="item.compliancePercentage"
                    height="10"
                    color="#e7541e"
                    rounded
                  />
                </v-sheet>
              </div>
              <div class="pl-2">{{ item.compliancePercentage }}%</div>
            </div>
          </template>
        </v-data-table>
      </v-sheet>
    </v-window-item>

    <v-window-item value="AppView">
      <AppView
        v-if="selectedTab === 'AppView'"
        @back="selectedTab = 'Overview'"
        @UpdateCommands="
          getLicense(selectedLicense._id);
          getDeviceCommands();
        "
        :app="selectedApp"
        :apps="apps"
        :devices="devices"
        :license="selectedLicense"
        :commands="commands"
      />
    </v-window-item>

    <v-window-item value="Loading">
      <div>
        <h1>Loading Data...</h1>
        <v-row>
          <v-col cols="3">
            <LoadingStatus v-model="loadingDevices" title="Devices: " />
          </v-col>
          <v-col cols="3">
            <LoadingStatus v-model="loadingApps" title="Apps: " />
          </v-col>
          <v-col cols="3">
            <LoadingStatus v-model="loadingReport" title="Report: " />
          </v-col>
          <v-col cols="3">
            <LoadingStatus v-model="loadingCommands" title="Commands: " />
          </v-col>
        </v-row>
      </div>
    </v-window-item>
  </v-window>

  <template v-else>
    <v-row>
      <v-col>
        <h1 v-if="appBar.items.length > 1">Select Patching License Above</h1>
        <h1 v-else>Loading please wait...</h1>
      </v-col>
    </v-row>
  </template>
</template>
